import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  afterNextRender,
  computed,
  contentChildren,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { DirtyFormRegistry } from '@core/services/dirty-form-registry.service';
import { PermissionService } from '@shared/services/permission.service';
import { IconComponent } from '../icon/icon.component';
import { TabContentDirective } from './tab-content.directive';

export interface HorizontalTab {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  /**
   * Permission key(s) gating visibility; omit/null = always visible. Pass an array to require ANY one
   * of the keys. Denied tabs are hidden entirely.
   */
  permission?: string | string[] | null;
  /** Optional numeric badge rendered after the label (e.g. a count). Hidden when null/undefined. */
  count?: number | null;
}

@Component({
  selector: 'app-horizontal-tabs',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './horizontal-tabs.component.html',
  styleUrl: './horizontal-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HorizontalTabsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() tabs: HorizontalTab[] = [];
  @Input() statePersist = false;
  @Input() queryParamKey = 'tab';
  @Input() lazy = true;
  @Input() guardUnsavedChanges = true;

  /**
   * Which way the strip runs. Horizontal is the default and what every existing caller gets; vertical
   * stands the tabs in a rail down the left with the panel beside them, which suits a settings-style
   * screen where the labels are a stable menu rather than sections of one record.
   *
   * Everything else is shared deliberately — route persistence, lazy panels, the unsaved-changes prompt
   * and the scroll affordance are the same behaviour either way, and a second component would have had
   * to keep all of it in step. (The class name predates this input; a rename to PageTabs is a tidy-up
   * for when nothing else is in flight against this file.)
   */
  @Input() orientation: 'horizontal' | 'vertical' = 'horizontal';

  @Input()
  set activeTabId(value: string) {
    const next = value ?? '';
    if (next === this.activeId()) return;
    this.activeId.set(next);
    // A tab the page switches to in code is as much "which tab you are on" as one that was clicked, so
    // it belongs in the URL the same way. Without this, a page that moves the user itself leaves the
    // route pointing at the tab they left, and a reload drops them back on it. Only after ngOnInit has
    // had its say: before that, this setter is the initial binding, and the route is what wins.
    if (this.initialised) this.persist(next);
    this.scrollActiveIntoView();
  }
  get activeTabId(): string {
    return this.activeId();
  }

  @Output() activeTabIdChange = new EventEmitter<string>();
  @Output() tabChange = new EventEmitter<string>();

  @ViewChild('strip') private readonly strip?: ElementRef<HTMLElement>;

  readonly tabContents = contentChildren(TabContentDirective);

  readonly activeId = signal('');
  readonly overflowing = signal(false);
  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);

  readonly activeTemplate = computed(() => {
    const id = this.activeId();
    return this.tabContents().find((c) => c.tabId === id)?.template ?? null;
  });

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dirtyForms = inject(DirtyFormRegistry);
  private readonly permissions = inject(PermissionService);
  private resizeObserver?: ResizeObserver;

  /** False until the opening tab is settled, so the initial binding cannot overwrite the route's tab. */
  private initialised = false;

  /** Tabs the current user may see — a tab with a `permission` the user lacks is dropped entirely. */
  get visibleTabs(): HorizontalTab[] {
    return this.tabs.filter((t) => this.isTabPermitted(t));
  }

  private isTabPermitted(tab: HorizontalTab): boolean {
    if (!tab.permission) return true;
    return Array.isArray(tab.permission)
      ? this.permissions.hasAny(tab.permission)
      : this.permissions.has(tab.permission);
  }

  constructor() {
    afterNextRender(() => {
      this.updateScrollState();
      document.fonts?.ready?.then(() => this.updateScrollState());
    });
    effect(() => {
      this.tabContents();
      queueMicrotask(() => this.updateScrollState());
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateScrollState();
  }

  ngOnInit(): void {
    this.selectInitialTab();
    this.initialised = true;
  }

  private selectInitialTab(): void {
    if (this.statePersist) {
      const fromRoute = this.route.snapshot.queryParamMap.get(this.queryParamKey);
      if (fromRoute && this.visibleTabs.some((t) => t.id === fromRoute && !t.disabled)) {
        this.activeId.set(fromRoute);
        queueMicrotask(() => {
          this.activeTabIdChange.emit(fromRoute);
          this.tabChange.emit(fromRoute);
        });
        return;
      }
    }
    // Fall back to the first visible tab when no valid tab is selected (e.g. the bound id is for a
    // permission-hidden tab, or none was supplied) — so callers can leave activeTabId unset.
    this.ensureValidActive();
    if (this.statePersist && this.activeId()) this.persist(this.activeId());
  }

  private ensureValidActive(): void {
    const visible = this.visibleTabs;
    if (visible.length === 0) return;
    const current = this.activeId();
    if (current && visible.some((t) => t.id === current && !t.disabled)) return;
    const first = visible.find((t) => !t.disabled) ?? visible[0];
    this.activeId.set(first.id);
    queueMicrotask(() => {
      this.activeTabIdChange.emit(first.id);
      this.tabChange.emit(first.id);
    });
  }

  ngAfterViewInit(): void {
    const el = this.strip?.nativeElement;
    if (!el) return;
    this.resizeObserver = new ResizeObserver(() => this.updateScrollState());
    this.resizeObserver.observe(el);
    this.updateScrollState();
    this.scrollActiveIntoView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabs'] && !changes['tabs'].firstChange) {
      queueMicrotask(() => this.updateScrollState());
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  select(tab: HorizontalTab): void {
    if (tab.disabled || tab.id === this.activeId()) return;
    if (!this.guardUnsavedChanges) {
      this.applySelect(tab);
      return;
    }
    this.dirtyForms
      .confirmLeave()
      .pipe(take(1))
      .subscribe((proceed) => {
        if (proceed) this.applySelect(tab);
      });
  }

  private applySelect(tab: HorizontalTab): void {
    this.activeId.set(tab.id);
    this.activeTabIdChange.emit(tab.id);
    this.tabChange.emit(tab.id);
    this.persist(tab.id);
    this.scrollActiveIntoView();
  }

  scroll(direction: number): void {
    const el = this.strip?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: 'smooth' });
  }

  onScroll(): void {
    this.updateScrollState();
  }

  private persist(id: string): void {
    if (!this.statePersist) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.queryParamKey]: id },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private updateScrollState(): void {
    const el = this.strip?.nativeElement;
    if (!el) return;
    const overflowing = el.scrollWidth - el.clientWidth > 1;
    this.overflowing.set(overflowing);
    this.canScrollLeft.set(el.scrollLeft > 1);
    this.canScrollRight.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }

  private scrollActiveIntoView(): void {
    queueMicrotask(() => {
      const active = this.strip?.nativeElement.querySelector<HTMLElement>('.ht-tab.active');
      active?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
      this.updateScrollState();
    });
  }
}
